import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Tag, User } from "@phosphor-icons/react";
import { getBlogPost, formatDate } from "../data/blog-posts";

function renderMarkdown(content: string) {
  const blocks = content.split("\n\n");
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith("## ")) {
      return (
        <h2
          key={i}
          className="mt-10 mb-4 text-2xl font-bold text-slate-900 dark:text-white"
        >
          {trimmed.slice(3)}
        </h2>
      );
    }

    if (trimmed.startsWith("### ")) {
      return (
        <h3
          key={i}
          className="mt-8 mb-3 text-xl font-semibold text-slate-900 dark:text-white"
        >
          {trimmed.slice(4)}
        </h3>
      );
    }

    if (/^\d+\.\s/.test(trimmed)) {
      const items = trimmed.split("\n").filter((l) => l.trim());
      return (
        <ol
          key={i}
          className="my-4 list-decimal space-y-2 pl-6 text-slate-600 dark:text-slate-300"
        >
          {items.map((item, j) => (
            <li
              key={j}
              dangerouslySetInnerHTML={{
                __html: formatInline(item.replace(/^\d+\.\s*/, "")),
              }}
            />
          ))}
        </ol>
      );
    }

    if (trimmed.startsWith("- ")) {
      const items = trimmed.split("\n").filter((l) => l.trim());
      return (
        <ul
          key={i}
          className="my-4 list-disc space-y-2 pl-6 text-slate-600 dark:text-slate-300"
        >
          {items.map((item, j) => (
            <li
              key={j}
              dangerouslySetInnerHTML={{
                __html: formatInline(item.replace(/^-\s*/, "")),
              }}
            />
          ))}
        </ul>
      );
    }

    return (
      <p
        key={i}
        className="my-4 leading-relaxed text-slate-600 dark:text-slate-300"
        dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }}
      />
    );
  });
}

function formatInline(text: string): string {
  return text
    .replace(
      /\*\*(.+?)\*\*/g,
      '<strong class="font-semibold text-slate-900 dark:text-white">$1</strong>',
    )
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;

  if (!post) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center pt-28 pb-20">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Post not found
          </h1>
          <Link
            to="/blog"
            className="mt-4 inline-flex items-center gap-2 text-primary-500 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-28 pb-20">
      <article className="mx-auto max-w-3xl px-6">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-primary-500/5 dark:bg-primary-500/10 px-2.5 py-0.5 text-xs font-medium text-primary-500 dark:text-primary-300"
            >
              <Tag size={10} weight="bold" />
              {tag}
            </span>
          ))}
        </div>

        <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          {post.title}
        </h1>

        <div className="mt-6 flex flex-wrap items-center gap-4 border-b border-slate-200 dark:border-slate-800 pb-6 text-sm text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <User size={14} />
            {post.author}
            <span className="text-slate-300 dark:text-slate-600">&middot;</span>
            {post.authorRole}
          </span>
          <span>{formatDate(post.date)}</span>
          <span className="inline-flex items-center gap-1">
            <Clock size={14} />
            {post.readTime}
          </span>
        </div>

        <div className="mt-8">{renderMarkdown(post.content)}</div>

        <div className="mt-16 border-t border-slate-200 dark:border-slate-800 pt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft size={16} />
            Back to all posts
          </Link>
        </div>
      </article>
    </main>
  );
}
